 
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { toast } from 'react-toastify';

import { env } from '@constants/index';
import type { iApiResponse } from '@interfaces';
import { AuthStorage } from '@utils/auth';

const BASE_URL = env.baseUrl;

// 인증이 필요 없는 API 경로 목록
const PUBLIC_ENDPOINTS = ['/auth/login', '/auth/refresh-token'];

// www-authenticate 헤더에서 JWT 만료 여부 확인
const isJwtExpiredFromHeader = (error: unknown): boolean => {
  const axiosError = error as { response?: { headers?: { 'www-authenticate'?: string } } };
  const wwwAuthenticate = axiosError?.response?.headers?.['www-authenticate'];
  if (wwwAuthenticate) {
    return wwwAuthenticate.includes('invalid_token') && wwwAuthenticate.includes('Jwt expired');
  }
  return false;
};

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// 토큰 갱신 큐 관리 (동시 요청 시 중복 refresh 방지)
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token!);
    }
  });
  failedQueue = [];
};

const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    // 인증이 필요 없는 API는 토큰을 붙이지 않음
    const isPublicEndpoint = PUBLIC_ENDPOINTS.some((endpoint) => config.url?.includes(endpoint));

    if (isPublicEndpoint) {
      return config;
    }

    // 일반 요청: 토큰 붙이기
    const token = AuthStorage.getToken();
    const sessionID = AuthStorage.getSessionID();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      if (navigator?.userAgent && sessionID) {
        config.headers.SessionID = sessionID;
      }
    }
    return config;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error) => {
      const originalRequest = error.config as CustomAxiosRequestConfig;

      // 세션 무효화 처리: 모든 인증 정보 제거 후 페이지 새로고침
      if (error?.response?.data?.keyError === 'SESSION_INVALID') {
        AuthStorage.clearAuth();
        window.location.reload();
        return false;
      }

      // www-authenticate 헤더에서 JWT 만료 여부 확인
      const jwtExpired = isJwtExpiredFromHeader(error);

      // 401 Unauthorized 또는 JWT 만료 시 토큰 갱신 시도
      if ((error.response?.status === 401 || jwtExpired) && !originalRequest._retry) {
        if (process.env.NODE_ENV !== 'production' && jwtExpired) {
          console.log('JWT expired detected from www-authenticate header');
        }
        // refresh-token 요청 자체가 실패한 경우 무한 루프 방지
        if (originalRequest.url?.includes('/auth/refresh-token')) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('Refresh token failed', error);
            toast.error('세션이 만료되었습니다. 다시 로그인해주세요.');
          }
          AuthStorage.clearAuth();
          // refresh-token 실패 시 현재 경로를 redirect query parameter로 전달
          const currentPath = window.location.pathname + window.location.search;
          if (currentPath !== '/login') {
            const redirectParam = encodeURIComponent(currentPath).replace(/%2F/g, '/');
            window.location.href = `/login?redirect=${redirectParam}`;
          } else {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }

        // 이미 토큰 갱신 중이면 큐에 추가하고 대기
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshToken = AuthStorage.getRefreshToken();

          // refreshToken이 없으면 바로 로그아웃
          if (!refreshToken) {
            if (process.env.NODE_ENV !== 'production') {
              toast.error('인증 정보가 없습니다. 다시 로그인해주세요.');
            }
            processQueue(new Error('No refresh token available'), null);
            AuthStorage.clearAuth();
            // 현재 경로를 redirect query parameter로 전달
            const currentPath = window.location.pathname + window.location.search;
            if (currentPath !== '/login') {
              const redirectParam = encodeURIComponent(currentPath).replace(/%2F/g, '/');
              window.location.href = `/login?redirect=${redirectParam}`;
            } else {
              window.location.href = '/login';
            }
            return Promise.reject(new Error('No refresh token available'));
          }

          // refresh-token 요청 시 무효한 accessToken을 제거하고 요청
          // (무효한 토큰이 있으면 인터셉터가 헤더에 추가하므로)
          const currentToken = AuthStorage.getToken();
          AuthStorage.setToken(''); // 임시로 비움

          const res = await instance.post(`${BASE_URL}/auth/refresh-token`, { refreshToken });

          if (res.status === 200) {
            const { accessToken, refreshToken: newRefreshToken } = res.data;
            // refreshToken 먼저 저장하여 cookie 만료 시간 설정
            AuthStorage.setRefreshToken(newRefreshToken);
            AuthStorage.setToken(accessToken);

            if (process.env.NODE_ENV !== 'production') {
              console.log('Token refreshed successfully');
            }

            // 대기 중인 요청들에게 새 토큰 전달
            processQueue(null, accessToken);

            // 갱신된 토큰으로 원래 요청 재시도
            return instance(originalRequest);
          } else {
            // 실패 시 원래 토큰 복구 (refreshToken의 만료 시간 사용)
            if (currentToken) AuthStorage.setToken(currentToken);
            processQueue(new Error('Token refresh failed'), null);
            return Promise.reject(new Error('Token refresh failed'));
          }
        } catch (_error) {
          // 대기 중인 요청들에게 에러 전파
          processQueue(_error, null);
          // 토큰 갱신 실패 시 모든 인증 정보 제거 후 로그인 페이지로
          if (process.env.NODE_ENV !== 'production') {
            console.log('Token refresh failed', _error);
            toast.error('세션이 만료되었습니다. 다시 로그인해주세요.');
          }
          AuthStorage.clearAuth();
          // 현재 경로를 redirect query parameter로 전달
          const currentPath = window.location.pathname + window.location.search;
          if (currentPath !== '/login') {
            const redirectParam = encodeURIComponent(currentPath).replace(/%2F/g, '/');
            window.location.href = `/login?redirect=${redirectParam}`;
          } else {
            window.location.href = '/login';
          }
          return Promise.reject(_error);
        } finally {
          isRefreshing = false;
        }
      }

      // Blob 응답 타입 에러 처리
      if (originalRequest.responseType === 'blob') {
        if (error && error.response) {
          error.response.data = JSON.parse(await error.response.data.text());
        }
        return Promise.reject(error);
      }

      // 개발 환경에서 에러 로깅 및 토스트 메시지 표시 (401은 위에서 처리되므로 제외)
      if (process.env.NODE_ENV !== 'production' && error.response?.status !== 401) {
        console.log('interceptors.response', error);
        toast.error(error.message);
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

export const API = createAxiosInstance();

/**
 * iApiResponse<T>로 래핑된 API 헬퍼
 * 사용: fetcher.get<iDevRequest[]>('/v1/dev-request') -> iApiResponse<iDevRequest[]>
 */
export const fetcher = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    API.get<iApiResponse<T>>(url, config).then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    API.post<iApiResponse<T>>(url, data, config).then((res) => res.data),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    API.put<iApiResponse<T>>(url, data, config).then((res) => res.data),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    API.patch<iApiResponse<T>>(url, data, config).then((res) => res.data),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    API.delete<iApiResponse<T>>(url, config).then((res) => res.data),
};

export default API;
